import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import './ep.css';
import { epApi, showError, isPlanRequired } from './api';
import TaskModal from './components/TaskModal';
import EpNav from './components/EpNav';
import DeadlineOverride from './components/DeadlineOverride';
import UpgradeModal from './components/UpgradeModal';
import {
  asksFor,
  COMPETITIVENESS,
  DECISION_LABEL,
  deadlineText,
  deliveryText,
  gradesText,
  housingText,
  isFuture,
  md,
  mdy,
  PLAN_LABEL,
  sessionsPrice,
  programName,
  sessionPrice,
  STAGES,
  TYPE_LABEL,
  weeksText,
  UNKNOWN_COST,
} from './format';
import { useEpUser, useLoad } from './hooks';
import { Plan, PlanView, ProgramCard, ProgramSession, TaskView } from './types';

const PLANS: Plan[] = ['interested', 'applying', 'applied', 'attending'];
const STAGE_ORDER = STAGES.map(([s]) => s);

/**
 * One program: what it is, its sessions, and the student's plan and checklist.
 * studentId/readOnly let the counselor and parent tabs show the same page for a linked student.
 */
export default function ProgramPage({ studentId: forStudent, readOnly, studentName }: { studentId?: number; readOnly?: boolean; studentName?: string } = {}) {
  const history = useHistory();
  const loc = useLocation();
  const qs = new URLSearchParams(loc.search);
  const programId = Number(qs.get('id') ?? 0);
  const me = useEpUser();
  // Counselors and parents open a program for a linked student: ?id=&student=&name=
  const linkedStudent = !me.isStudent && qs.get('student') ? Number(qs.get('student')) : null;
  const studentId = forStudent ?? linkedStudent ?? me.userId;
  readOnly = readOnly || !!linkedStudent || !me.isStudent;
  studentName = studentName || qs.get('name') || undefined;
  const canEdit = !readOnly && me.isStudent;

  const program = useLoad<ProgramCard>(() => epApi.program(programId), [programId]);
  const sessions = useLoad<ProgramSession[]>(() => epApi.sessions(programId), [programId]);
  const topics = useLoad<{ slug: string; name: string }[]>(() => epApi.programTopics(programId), [programId]);
  const plan = useLoad<PlanView | null>(() => ((me.isStudent || linkedStudent || forStudent) && studentId ? epApi.plan(studentId, programId) : Promise.resolve(null)), [studentId, programId]);

  const [busy, setBusy] = useState(false);
  const [openTask, setOpenTask] = useState<TaskView | null>(null);
  const [upgrade, setUpgrade] = useState<number | null>(null);
  const [portalOpen, setPortalOpen] = useState(false);
  const [newTask, setNewTask] = useState<{ title: string; date: string } | null>(null);
  // "Notify me when applications open": only for a student, only while no dates are posted.
  const follows = useLoad<{ program_id: number }[]>(() => (canEdit ? epApi.follows() : Promise.resolve([])), [canEdit]);
  const following = (follows.data ?? []).some((f) => Number(f.program_id) === programId);
  const addActivity = async () => {
    setBusy(true);
    try {
      const r = await epApi.addToActivities(studentId, programId);
      toast.success(r.added ? 'Added to Activities. Finish the description and hours there.' : 'Already in your Activities. Opening it.');
      plan.reload();
      history.push(r.link);
    } catch {
      /* services/http already showed the message */
    } finally {
      setBusy(false);
    }
  };
  const toggleFollow = async () => {
    setBusy(true);
    try {
      if (following) {
        await epApi.unfollow(programId);
        toast.success('You will not get a notice for this program.');
      } else {
        await epApi.follow(programId);
        toast.success("We'll tell you when this program posts its dates.");
      }
      follows.reload();
    } catch {
      /* services/http already showed the message */
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (programId) epApi.recordView(programId);
  }, [programId]);

  const p = program.data;
  const pv = plan.data;
  const sess = useMemo(() => sessions.data ?? [], [sessions.data]);
  const chosen = sess.find((s) => s.id === pv?.session_id) ?? (sess.length === 1 ? sess[0] : null);

  const choosePlan = async (next: Plan, opts: { session_id?: number | null; deadline_choice?: string | null } = {}) => {
    if (!canEdit || busy) return;
    setBusy(true);
    try {
      const v = await epApi.setPlan(studentId, programId, { plan: next, session_id: opts.session_id ?? pv?.session_id ?? chosen?.id ?? null, deadline_choice: opts.deadline_choice ?? pv?.deadline_choice ?? null });
      plan.setData(v);
      toast.success(pv?.plan ? 'Plan updated. Your checklist shows on your dashboard and calendar.' : 'Added to My programs. Your checklist shows on your dashboard and calendar.');
    } catch (e) {
      if (isPlanRequired(e)) setUpgrade((e as any).response.data.limit ?? 2);
      else showError(e);
    } finally {
      setBusy(false);
    }
  };

  const tick = async (t: TaskView) => {
    if (!t.can_edit || !pv) return;
    const status = t.status === 'done' ? 'not_started' : 'done';
    try {
      const saved = await epApi.updateTask(studentId, t.id, { status });
      replaceTask(saved);
    } catch (e) {
      showError(e);
    }
  };

  const replaceTask = (t: TaskView) => {
    // Counts and next step come from the server; reload the plan quietly.
    if (pv) plan.setData({ ...pv, checklist: pv.checklist.map((x) => (x.id === t.id ? t : x)) });
    plan.reload();
  };

  const addTask = async () => {
    if (!newTask?.title.trim()) return;
    try {
      await epApi.addTask(studentId, { title: newTask.title.trim(), program_id: programId, student_target_date: newTask.date || undefined });
      setNewTask(null);
      plan.reload();
      toast.success('Added to your checklist');
    } catch (e) {
      showError(e);
    }
  };

  const decide = async (decision: string) => {
    try {
      plan.setData(await epApi.setDecision(studentId, programId, decision));
      toast.success('Decision saved');
    } catch (e) {
      showError(e);
    }
  };

  const nextDeadline = useMemo(() => {
    const ds = sess.flatMap((s) => [s.priority_deadline, s.deadline_date]).filter((d): d is string => !!d && isFuture(d)).sort();
    return ds[0] ?? null;
  }, [sess]);

  if (!programId) return <div className="ep"><p>That program link is missing its number.</p></div>;
  if (program.error) {
    return (
      <div className="ep">
        <div className="card empty">
          <h3>We could not open this program</h3>
          <p style={{ marginTop: 6 }}>It may have been removed. Search again to find similar programs.</p>
          <button type="button" className="btn" style={{ marginTop: 14 }} onClick={() => history.push('/summer-programs')}>Back to programs</button>
        </div>
      </div>
    );
  }
  if (!p) return <div className="ep"><p className="muted">Loading the program</p></div>;

  const name = programName(p);
  const comp = p.competitivenessLabel ? COMPETITIVENESS[p.competitivenessLabel] : null;
  const price = sessionsPrice(sess, p);
  const asks = asksFor(p);
  const stageIdx = pv ? STAGE_ORDER.indexOf(pv.stage) : -1;
  const deadlineNote = deadlineText(p.deadlineKind, nextDeadline, p.lastYearDeadlineDate);

  return (
    <div className="ep">
      <div className="between" style={{ marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" className="linkish" onClick={() => history.goBack()}>
          ← Back
        </button>
        {me.isStudent && !forStudent && !linkedStudent && <EpNav active={null} studentId={me.userId} />}
        {linkedStudent && <EpNav active={null} studentId={linkedStudent} forStudent={{ name: qs.get('name') || studentName || 'the student' }} />}
      </div>
      <div className="pp">
        <div>
          <section className="card" aria-label="About the program">
            <h1>{name}</h1>
            {p.institution && <p className="muted" style={{ marginTop: 4 }}>Run by {p.institution}</p>}
            {p.description && <p className="body" style={{ marginTop: 12, maxWidth: 680 }}>{p.description}</p>}
            <div className="row" style={{ marginTop: 12 }}>
              {(topics.data ?? p.topics ?? []).map((t) => <span key={t.slug} className="chip">{t.name}</span>)}
              {p.programType && <span className="chip">{TYPE_LABEL[p.programType]}</span>}
              {comp && <span className={`chip ${comp.tone}`} title={comp.help}>{comp.label}</span>}
              {p.whenOffered && <span className="chip">{p.whenOffered === 'summer' ? 'Summer' : 'Year-round'}</span>}
              {p.paysStudents && <span className="chip ok">Pays students</span>}
            </div>
            <div className="body" style={{ marginTop: 12, lineHeight: 1.8 }}>
              <div>
                {price.free ? <span className="chip ok">Free</span> : <span>{price.text}</span>}
                {p.financialAidAvailable ? ' · Financial aid available' : ''}
              </div>
              <div>{[deliveryText(sess.length ? sess.map((s) => s.delivery_mode) : [p.deliveryMode]), sess.length > 1 ? `${sess.length} sessions` : [p.city, p.stateName].filter(Boolean).join(', '), weeksText(p.durationWeeksMin, p.durationWeeksMax)].filter(Boolean).join(' · ')}</div>
              {gradesText(p.gradesEligible) && <div>{gradesText(p.gradesEligible)}</div>}
              {comp && <div>{comp.label}: {comp.help.charAt(0).toLowerCase() + comp.help.slice(1)}.</div>}
              {eligibilityExtra(p.eligibilityDetails, !!gradesText(p.gradesEligible)) && <div>{eligibilityExtra(p.eligibilityDetails, !!gradesText(p.gradesEligible))}</div>}
            </div>
            <p className="muted small" style={{ marginTop: 10 }}>
              From GradMap{p.updatedAt ? ` · last checked ${mdy(p.updatedAt)}` : ''} ·{' '}
              {p.website ? <a href={p.website} target="_blank" rel="noopener noreferrer">check the program site to confirm</a> : 'check the program site to confirm'}
            </p>
          </section>

          {pv?.plan && (
            <section className="card" aria-label="My application">
              <div className="between">
                <h2>{readOnly ? `${studentName || 'Student'}'s application` : 'My application'}</h2>
                <span className="muted small">{`${pv.done_count} of ${pv.total_count} done`}</span>
              </div>
              <div className="stagebar" aria-label="Stage">
                {STAGES.map(([s, l], i) => (
                  <span key={s} className={i < stageIdx ? 'done' : i === stageIdx ? 'now' : ''} aria-current={i === stageIdx ? 'step' : undefined}>
                    {i < stageIdx ? `✓ ${l}` : l}
                  </span>
                ))}
              </div>
              {pv.next_step && (
                <div className="nextbox">
                  <div>
                    <div className="k">NEXT STEP</div>
                    <div style={{ fontWeight: 500, marginTop: 2 }}>
                      {pv.next_step.title}
                      {pv.next_step.date ? <span className="muted small">{` · ${pv.next_step.student_target_date ? (readOnly ? 'target date' : 'your date') : 'deadline'} ${md(pv.next_step.date)}`}</span> : null}
                    </div>
                  </div>
                  <button type="button" className="btn sm primary" onClick={() => setOpenTask(pv.next_step)}>Open</button>
                </div>
              )}
              <div className="portal">
                <div style={{ minWidth: 0 }}>
                  <span className="lbl" style={{ marginBottom: 2 }}>{readOnly ? `Where ${studentName || 'the student'} applies` : 'Where you apply'}</span>
                  {pv.portal_url ? (
                    <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <a href={/^https?:/i.test(pv.portal_url) ? pv.portal_url : `https://${pv.portal_url}`} target="_blank" rel="noopener noreferrer">
                        {pv.portal_url.replace(/^https?:\/\//i, '')}
                      </a>
                      {pv.portal_username ? <span className="body">{` · username ${pv.portal_username}`}</span> : null}
                    </div>
                  ) : (
                    <div className="body">{canEdit ? 'Add the application link and your username' : 'Not added yet'}</div>
                  )}
                </div>
                {canEdit && (
                  <button type="button" className="btn sm" onClick={() => setPortalOpen(true)}>
                    {pv.portal_url ? 'Edit' : 'Add'}
                  </button>
                )}
              </div>
              {pv.update_notice && me.isStudent && (
                <div className="panel" role="status" style={{ margin: '12px 0' }}>
                  <b style={{ fontWeight: 500 }}>{name} changed your list:</b>
                  {` ${[pv.update_notice.added ? `${pv.update_notice.added} new ${pv.update_notice.added === 1 ? 'task' : 'tasks'}` : '', pv.update_notice.dates ? `${pv.update_notice.dates} ${pv.update_notice.dates === 1 ? 'date' : 'dates'} updated` : ''].filter(Boolean).join(', ')}.`}
                  {pv.update_notice.deadline ? ` Deadline: ${mdy(pv.update_notice.deadline)}.` : ''}
                  <button
                    type="button"
                    className="btn sm"
                    style={{ marginLeft: 10 }}
                    onClick={async () => {
                      try {
                        await epApi.noticeSeen(studentId, programId);
                        plan.setData({ ...pv, update_notice: null });
                      } catch (e) {
                        showError(e);
                      }
                    }}
                  >
                    Got it
                  </button>
                </div>
              )}
              <div role="list" aria-label="Checklist">
                {pv.checklist.filter((t) => t.status !== 'not_applicable').map((t) => (
                  <div key={t.id} role="listitem" className={`task ${t.status === 'done' ? 'done' : ''}`}>
                    <button
                      type="button"
                      className={`tick ${t.status === 'done' ? 'on' : ''}`}
                      aria-label={t.status === 'done' ? `Mark "${t.title}" not done` : `Mark "${t.title}" done`}
                      aria-pressed={t.status === 'done'}
                      disabled={!t.can_edit || readOnly}
                      onClick={() => tick(t)}
                    >
                      {t.status === 'done' ? '✓' : ''}
                    </button>
                    <div style={{ minWidth: 0 }}>
                      <div className="row" style={{ gap: 6 }}>
                        <span className="t" role="button" tabIndex={0} onClick={() => setOpenTask(t)} onKeyDown={(e) => e.key === 'Enter' && setOpenTask(t)}>{t.title}</span>
                        {t.created_by_role === 'counselor' && <span className="chip a">From your counselor</span>}
                      </div>
                      <TaskSubline t={t} onOpen={() => setOpenTask(t)} canEdit={canEdit && t.can_edit} onAddActivity={addActivity} busy={busy} />
                    </div>
                    <div className="date">
                      {t.status === 'done' ? (
                        <span className="muted">Done</span>
                      ) : t.date ? (
                        <span className={t.student_target_date ? 'date mine' : 'date official'}>{`${t.student_target_date ? (readOnly ? 'Target date' : 'Your date') : 'Deadline'} ${md(t.date)}`}</span>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
              {canEdit && (
                <div className="between" style={{ marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                  {newTask ? (
                    <div className="row" style={{ flex: 1 }}>
                      <input className="in" style={{ flex: '1 1 220px' }} autoFocus placeholder="What do you need to do?" aria-label="New task" value={newTask.title} maxLength={255} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && addTask()} />
                      <input className="in" style={{ width: 170 }} type="date" aria-label="Your date" value={newTask.date} onChange={(e) => setNewTask({ ...newTask, date: e.target.value })} />
                      <button type="button" className="btn primary" onClick={addTask}>Add</button>
                      <button type="button" className="btn" onClick={() => setNewTask(null)}>Cancel</button>
                    </div>
                  ) : (
                    <button type="button" className="btn ghost" onClick={() => setNewTask({ title: '', date: '' })}>+ Add my own task</button>
                  )}
                  <span className="muted small">Open any task to add details. Blue is your date, red is the program's deadline.</span>
                </div>
              )}
              {canEdit && (pv.stage === 'submitted' || pv.stage === 'decision') && (
                <div className="panel" style={{ marginTop: 14 }}>
                  <div style={{ fontWeight: 500, marginBottom: 8 }}>{pv.decision ? `Decision: ${DECISION_LABEL[pv.decision]}` : 'Heard back? Record the decision.'}</div>
                  <div className="row">
                    {Object.entries(DECISION_LABEL).map(([k, l]) => (
                      <button key={k} type="button" className={`chipb ${pv.decision === k ? 'on' : ''}`} aria-pressed={pv.decision === k} onClick={() => decide(k)}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          <section className="card" aria-label="Sessions">
            <h2>Sessions</h2>
            <p className="muted small" style={{ marginTop: 4 }}>
              {sess.length === 0 ? 'No sessions listed yet.' : sess.length === 1 ? 'One session.' : `${sess.length} sessions. ${pv?.plan && canEdit ? 'Pick the one you are applying to.' : ''}`}
            </p>
            <div style={{ marginTop: 8 }}>
              {sess.map((s) => {
                const sp = sessionPrice(s);
                const picked = chosen?.id === s.id;
                const pick = () => canEdit && pv?.plan && sess.length > 1 && !picked && choosePlan(pv.plan, { session_id: s.id });
                return (
                  <div key={s.id} className="srow" onClick={pick} role={sess.length > 1 && canEdit && pv?.plan ? 'radio' : undefined} aria-checked={sess.length > 1 ? picked : undefined} tabIndex={sess.length > 1 && canEdit && pv?.plan ? 0 : undefined} onKeyDown={(e) => e.key === 'Enter' && pick()}>
                    <input type="radio" readOnly checked={picked} tabIndex={-1} aria-hidden="true" style={{ marginTop: 4, visibility: sess.length > 1 || picked ? 'visible' : 'hidden' }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 500 }}>{s.label || [s.city, s.state_name].filter(Boolean).join(', ') || p.institution || name}</div>
                      <div className="body small" style={{ marginTop: 2 }}>
                        {[s.start_date ? `${md(s.start_date)}${s.end_date ? ` to ${md(s.end_date)}` : ''}` : s.dates_text, [s.city, s.state_name].filter(Boolean).join(', '), s.delivery_mode === 'online' ? 'Online' : housingText(s.housing_type), weeksText(s.duration_weeks_min, s.duration_weeks_max)].filter(Boolean).join(' · ')}
                      </div>
                      <div className="muted small" style={{ marginTop: 2 }}>
                        {isFuture(s.priority_deadline) ? `Priority deadline ${mdy(s.priority_deadline)} · ` : ''}
                        {deadlineText(s.deadline_kind ?? p.deadlineKind, isFuture(s.deadline_date) ? s.deadline_date : null, p.lastYearDeadlineDate)}
                      </div>
                      {picked && canEdit && pv?.plan && isFuture(s.priority_deadline) && (
                        <div className="row" style={{ marginTop: 8 }} onClick={(e) => e.stopPropagation()}>
                          <span className="small body">Aim for</span>
                          {[['regular', 'Regular deadline'], ['priority', 'Priority deadline']].map(([k, l]) => (
                            <button key={k} type="button" className={`chipb ${(pv.deadline_choice ?? 'regular') === k ? 'on' : ''}`} onClick={() => choosePlan(pv.plan!, { deadline_choice: k })}>
                              {l}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>{sp.free ? <span className="chip ok">Free</span> : sp.text === UNKNOWN_COST ? <span className="muted small">{UNKNOWN_COST}</span> : <span className="small" style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{sp.text}</span>}</div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        <aside className="card" aria-label="Plan">
          {readOnly ? (
            <>
              <h3>{studentName || 'The student'}'s plan</h3>
              <p className="body" style={{ marginTop: 8 }}>{pv?.plan ? PLAN_LABEL[pv.plan] : 'Not tracking this program yet.'}</p>
            </>
          ) : (
            <>
              <h3>What is your plan for this program?</h3>
              <div className="row" style={{ marginTop: 12 }} role="group" aria-label="Your plan">
                {PLANS.map((k) => (
                  <button key={k} type="button" className={`chipb ${pv?.plan === k ? 'on' : ''}`} aria-pressed={pv?.plan === k} disabled={busy || !canEdit} onClick={() => choosePlan(k)}>
                    {PLAN_LABEL[k]}
                  </button>
                ))}
              </div>
            </>
          )}
          <div className="panel" style={{ marginTop: 14 }}>
            {nextDeadline
              ? `The deadline is ${mdy(nextDeadline)}. Your checklist counts back from it, and GradMap suggests submitting 7 to 10 days early.`
              : `${deadlineNote}. ${p.lastYearDeadlineDate ? 'Target dates appear as soon as the program posts it, and you will get a notice.' : 'Target dates appear as soon as the program posts its dates.'}`}
          </div>
          {!nextDeadline && pv?.plan && (
            <DeadlineOverride
              studentId={studentId}
              programId={programId}
              override={pv.deadline_override ?? null}
              canSet={!pv.decision && (canEdit || (!!linkedStudent && me.role === 'counselor'))}
              viewer={canEdit ? 'student' : me.role === 'counselor' ? 'counselor' : 'parent'}
              studentName={studentName || qs.get('name') || undefined}
              onSaved={(v) => plan.setData(v)}
            />
          )}
          {canEdit && !nextDeadline && !pv?.plan && !['rolling', 'until_full', 'open'].includes(String(p.deadlineKind ?? '')) && (
            <div style={{ marginTop: 12 }}>
              <button type="button" className={`btn ${following ? '' : 'primary'}`} style={{ width: '100%' }} disabled={busy || follows.loading} aria-pressed={following} onClick={toggleFollow}>
                {following ? 'Following: we will tell you' : 'Notify me when applications open'}
              </button>
              <p className="body small" style={{ marginTop: 8, lineHeight: 1.55 }}>
                {following
                  ? 'You get a notice and an email when this program posts its dates. Tap again to stop.'
                  : 'Not ready to pick a plan? Get a notice and an email when this program posts its dates.'}
              </p>
            </div>
          )}
          {!readOnly && (
            <p className="body small" style={{ marginTop: 12, lineHeight: 1.55 }}>
              {pv?.plan
                ? 'Your checklist is under My application. Open any task to add a recommender, an essay draft, notes or a new date.'
                : 'Add it to Tracking (pick how far along you are) to get a checklist with dates. It shows on your dashboard and calendar, and reminders arrive 7 days and 1 day before each date.'}
            </p>
          )}
          {asks && (
            <>
              <hr style={{ border: 0, borderTop: '1px solid var(--ep-line)', margin: '16px 0' }} />
              <h3>What it asks for</h3>
              <p className="body small" style={{ marginTop: 8, lineHeight: 1.55 }}>The application asks for {asks}, as listed by the program.</p>
            </>
          )}
          {p.website && (
            <a className="btn" style={{ marginTop: 16, width: '100%' }} href={p.website} target="_blank" rel="noopener noreferrer">
              Program website
            </a>
          )}
        </aside>
      </div>

      {openTask && (
        <TaskModal task={openTask} studentId={studentId} programName={name} programDeadline={nextDeadline ?? (pv?.checklist ?? []).filter((t) => t.official_deadline && t.deadline_source === 'gradmap').map((t) => t.official_deadline as string).sort()[0] ?? null} onClose={() => setOpenTask(null)} onSaved={replaceTask} />
      )}
      {upgrade != null && <UpgradeModal max={upgrade} onClose={() => setUpgrade(null)} />}
      {portalOpen && pv && (
        <PortalModal
          url={pv.portal_url ?? ''}
          username={pv.portal_username ?? ''}
          onClose={() => setPortalOpen(false)}
          onSave={async (url, username) => {
            try {
              plan.setData(await epApi.setDetails(studentId, programId, { portal_url: url || null, portal_username: username || null }));
              setPortalOpen(false);
              toast.success('Saved');
            } catch (e) {
              showError(e);
            }
          }}
        />
      )}
    </div>
  );
}

function TaskSubline({ t, onOpen, canEdit, onAddActivity, busy }: { t: TaskView; onOpen: () => void; canEdit: boolean; onAddActivity?: () => void; busy?: boolean }) {
  const letter = /^rec_/.test(t.requirement_key ?? '');
  const essay = /^essay/.test(t.requirement_key ?? '');
  const statusText: Record<string, string> = { not_asked: 'Not asked yet', asked: 'Asked', agreed: 'Agreed', sent: 'Letter sent', declined: 'Said no' };
  if (letter) {
    if (t.recommender_name) {
      return <div className="body small">{[t.recommender_name, t.recommender_role].filter(Boolean).join(', ')}{t.recommender_status ? ` · ${statusText[t.recommender_status] ?? ''}` : ''}</div>;
    }
    return canEdit ? <button type="button" className="linkish small" onClick={onOpen}>+ Add who you are asking</button> : null;
  }
  if (essay) {
    const prompt = t.essay_prompt ? `"${t.essay_prompt.length > 70 ? `${t.essay_prompt.slice(0, 70)}...` : t.essay_prompt}"` : '';
    return (
      <div className="body small">
        {prompt || (canEdit ? <button type="button" className="linkish small" onClick={onOpen}>+ Add the prompt</button> : 'No prompt added')}
        {t.word_limit ? ` · ${t.word_limit} words` : ''}
        {' · '}
        {t.draft_link ? <a href={t.draft_link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Open my draft</a> : <span className="muted">No draft linked yet</span>}
      </div>
    );
  }
  if (t.requirement_key === 'attend:activity' && canEdit && onAddActivity) {
    return (
      <button type="button" className="linkish small" disabled={busy} onClick={(e) => { e.stopPropagation(); onAddActivity(); }}>
        {t.status === 'done' ? 'Open it in Activities' : '+ Add it to my Activities now'}
      </button>
    );
  }
  if (t.requirement_key === 'submit' && t.status !== 'done') return <div className="muted small">Aim to submit 7 to 10 days before the deadline.</div>;
  return null;
}

function PortalModal({ url, username, onClose, onSave }: { url: string; username: string; onClose: () => void; onSave: (url: string, username: string) => void }) {
  const [u, setU] = useState(url);
  const [n, setN] = useState(username);
  return (
    <div className="ep-modal-bg" role="dialog" aria-modal="true" aria-labelledby="ep-portal-title" onClick={onClose}>
      <div className="ep-modal ep" style={{ padding: 24, minHeight: 0 }} onClick={(e) => e.stopPropagation()}>
        <h2 id="ep-portal-title">Where you apply</h2>
        <div className="stack" style={{ marginTop: 14 }}>
          <div>
            <label className="lbl" htmlFor="ep-pu">Application link</label>
            <input id="ep-pu" className="in" autoFocus placeholder="apply.program.edu" value={u} onChange={(e) => setU(e.target.value)} maxLength={500} />
          </div>
          <div>
            <label className="lbl" htmlFor="ep-pn">Your username or email there</label>
            <input id="ep-pn" className="in" autoComplete="off" value={n} onChange={(e) => setN(e.target.value)} maxLength={120} />
          </div>
          <p className="muted small">Never store a password here. Use your browser or a password manager for that.</p>
        </div>
        <div className="foot">
          <button type="button" className="btn primary" onClick={() => onSave(u.trim() && !/^https?:\/\//i.test(u.trim()) ? `https://${u.trim()}` : u.trim(), n.trim())}>Save</button>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/**
 * The eligibility note without what the Grades line already says: sentences that only
 * name grades ("Grades 10 to 12.", "Open to rising juniors and seniors.") are dropped
 * when grades show above. Anything else (age, residency, GPA) stays.
 */
export function eligibilityExtra(note: string | null | undefined, gradesShown: boolean): string | null {
  const t = String(note ?? '').trim();
  if (!t) return null;
  if (!gradesShown) return t;
  const onlyGrades = (x: string) =>
    x.toLowerCase()
      .replace(/\b(open|to|for|students?|in|rising|current|grades?|and|or|through|th|freshm[ae]n|sophomores?|juniors?|seniors?|high|school|only|entering)\b/g, '')
      .replace(/[\d\s,.;:&()+\u2013-]/g, '') === '';
  const keep = t.split(/(?<=[.;])\s+/).filter((x) => !onlyGrades(x));
  return keep.join(' ').trim() || null;
}
